export type ReleaseLine = "release-avx256-rl10" | "release-rpm-el10";

/**
 * A release line (build track) the portal knows about, served by the backend's
 * release-line registry. Drives the admin build browser + upload form so adding a
 * new track (e.g. Windows) requires no frontend change.
 */
export interface ReleaseLineDef {
  id: string;
  label: string;
  osFamily: string;
  defaultOs: string;
  defaultArch: string;
}

export type PortalRole = "platform_admin" | "account_admin" | "member";

/** A portal-local account (admin console view). */
export interface Account {
  companyId: string;
  portalAccountId: string;
  name: string;
  releaseLines: ReleaseLine[];
  logsBucket: string;
  status: "active" | "suspended";
  /** Linked HubSpot Company id, when this account is linked to HubSpot. */
  hubspotCompanyId?: string;
}

/** A HubSpot company available to link a portal account to. */
export interface HubSpotCompany {
  companyId: string;
  name: string;
  domain?: string;
}

/** A user assigned to an account (admin console view). */
export interface PortalUser {
  contactId: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: PortalRole;
  uid?: string;
  status: "invited" | "active" | "deactivated";
}

export interface Me {
  email: string;
  role: PortalRole;
  account: {
    id: string;
    name: string;
    releaseLines: ReleaseLine[];
    status: "active" | "suspended";
  };
  /** True while this user still owes an emailed second factor before portal access. §F-8 */
  mfaRequired?: boolean;
}

export interface BuildArtifact {
  pipeline: ReleaseLine;
  kind?: "portal" | "selinux-policy";
  version: string;
  buildNumber: string;
  os: string;
  arch: string;
  objectPath: string;
  sha256: string;
  blake3: string;
  signature: { tool: string; signaturePath?: string; keyId?: string };
  sizeBytes: number;
  publishedAt: string;
  releaseNotesUrl?: string;
}

export interface DownloadUrlResponse {
  url: string;
  build: BuildArtifact;
  expiresInSeconds: number;
}

/** A published document in the shared customer documents library. */
export interface PortalDocument {
  id: string;
  title: string;
  description?: string;
  category?: string;
  objectPath: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  publishedAt: string;
  publishedBy: string;
}

export interface DocumentDownloadUrlResponse {
  url: string;
  document: PortalDocument;
  expiresInSeconds: number;
}

export type LicenseType = "hub" | "named_user";
export const LICENSE_TYPES: readonly LicenseType[] = ["hub", "named_user"];
export type LicenseStatus = "active" | "revoked";
export const LICENSE_MIN_TERM_YEARS = 1;
export const LICENSE_MAX_TERM_YEARS = 10;

export interface PortalLicense {
  id: string;
  type: LicenseType;
  productSku: string;
  count: number;
  termYears: number;
  customerName: string;
  customerId: string;
  portalAccountId?: string;
  companyId?: string;
  hubspotCompanyId?: string;
  activationStart: string;
  activationEnd: string;
  status: LicenseStatus;
  algorithm: string;
  pem: string;
  notes?: string;
  createdAt: string;
  createdByEmail: string;
  revokedAt?: string;
  revokedByEmail?: string;
}

export type LicenseSummary = Omit<PortalLicense, "pem">;

export interface UploadUrlResponse {
  url: string;
  bucket: string;
  objectPath: string;
  expiresInSeconds: number;
}

export interface AuditEvent {
  action: string;
  actorEmail?: string;
  portalAccountId?: string;
  target?: string;
  result: string;
  timestamp: string;
}

export interface BuildCandidate {
  pipeline: ReleaseLine;
  objectPath: string;
  sizeBytes: number;
  updated: string;
  ga: boolean;
  live: boolean;
  artifact?: BuildArtifact;
  promotedAt?: string;
  promotedBy?: string;
}

export interface UploadRecord {
  portalAccountId: string;
  contactId: string;
  bucket: string;
  objectPath: string;
  sizeBytes: number;
  checksum?: string;
  timestamp: string;
}

export type TicketStatus = "open" | "pending" | "in_progress" | "resolved" | "closed";
export type TicketPriority = "low" | "normal" | "high" | "urgent";
export type TicketCategory = "technical" | "bug" | "build" | "account" | "other";

export const TICKET_STATUSES: TicketStatus[] = [
  "open",
  "pending",
  "in_progress",
  "resolved",
  "closed",
];
export const TICKET_PRIORITIES: TicketPriority[] = ["low", "normal", "high", "urgent"];
export const TICKET_CATEGORIES: TicketCategory[] = [
  "technical",
  "bug",
  "build",
  "account",
  "other",
];

export type SlaState = "ok" | "at_risk" | "breached" | "met" | "paused";

export interface TicketAttachment {
  bucket: string;
  objectPath: string;
  filename: string;
  sizeBytes: number;
  uploadedByEmail: string;
  createdAt: string;
  scanStatus?: "pending" | "clean" | "infected";
}

export interface TicketComment {
  id: string;
  authorEmail: string;
  authorType: "customer" | "staff" | "ai";
  body: string;
  internal: boolean;
  mentions?: string[];
  /** Sanitized Markdown HTML of `body`, computed server-side at read time. §4 */
  renderedBody?: string;
  attachments: TicketAttachment[];
  createdAt: string;
}

/** A @mention typeahead candidate: a same-account teammate or a PumaMesh admin. */
export interface MentionCandidate {
  email: string;
  name: string;
  kind: "account" | "admin";
}

export interface Ticket {
  id: string;
  /** Server-allocated global case number; absent on legacy tickets until backfill. */
  number?: number;
  portalAccountId: string;
  companyId: string;
  accountName: string;
  subject: string;
  description: string;
  status: TicketStatus;
  priority: TicketPriority;
  category: TicketCategory;
  requesterEmail: string;
  requesterContactId: string;
  assigneeEmail?: string;
  shadowCaseId?: string;
  firstResponseDueAt?: string;
  resolutionDueAt?: string;
  firstRespondedAt?: string;
  slaState?: SlaState;
  /** Sanitized Markdown HTML of `description`, computed server-side at read time. §4 */
  renderedDescription?: string;
  tags?: string[];
  watchers?: string[];
  queue?: string;
  linkedBuildId?: string;
  mergedIntoId?: string;
  csatScore?: number;
  csatComment?: string;
  csatAt?: string;
  attachments: TicketAttachment[];
  comments: TicketComment[];
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
}

export interface NotificationPref {
  email: string;
  enabled: boolean;
}

/** A suggested self-help article shown before opening a case (KB deflection). §4 */
export interface KbArticle {
  id: string;
  title: string;
  summary: string;
}

export interface SavedView {
  id: string;
  ownerUid: string;
  name: string;
  filter: Record<string, unknown>;
  createdAt: string;
}

export interface CannedResponse {
  id: string;
  title: string;
  body: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface SupportSettings {
  queues: Record<string, string[]>;
}

export interface TicketMetrics {
  total: number;
  byStatus: Record<string, number>;
  byPriority: Record<string, number>;
  volumeByDay: Record<string, number>;
  avgFirstResponseMins: number | null;
  avgResolutionMins: number | null;
  backlogOpen: number;
  perAccount: Record<string, number>;
  perAgent: Record<string, number>;
  csatAvg: number | null;
  csatCount: number;
}

export type AccessRequestStatus = "pending" | "approved" | "denied";

export interface AccessRequest {
  id: string;
  email: string;
  name?: string;
  company?: string;
  message?: string;
  status: AccessRequestStatus;
  createdAt: string;
  decidedAt?: string;
  decidedBy?: string;
}
